import jwt_decode from 'jwt-decode';
import { LocalStorage } from 'src/services/LocalStorage';
import { AuthJwtObject, JwtInterface, jwtStorageKey } from './Auth.types';
import { isAuthJwtObject } from './Auth.guards';

class AuthUtils {
  private static readonly defaultState: AuthJwtObject = {
    token: '',
    expires: 0,
  };

  public static getJwt = (): AuthJwtObject => {
    const jwt = LocalStorage.get(jwtStorageKey, true);

    if (isAuthJwtObject(jwt)) {
      return jwt;
    }

    return AuthUtils.defaultState;
  };

  public static setJwt = (token: string | AuthJwtObject): void => {
    if (isAuthJwtObject(token)) {
      LocalStorage.set(jwtStorageKey, token);
      return;
    }

    try {
      LocalStorage.set(jwtStorageKey, AuthUtils.make(token));
    } catch (error) {
      console.error('[AuthUtils] Failed to set JWT:', error);
      // Clear any existing invalid JWT to force re-authentication
      AuthUtils.removeJwt();
      throw error; // Re-throw so callers know auth failed
    }
  };

  public static removeJwt = (): void => {
    LocalStorage.remove(jwtStorageKey);
  };

  public static isExpired = (jwt?: AuthJwtObject): boolean => {
    return Date.now() > (jwt?.expires ?? AuthUtils.getJwt().expires);
  };

  public static getPayload = (): null | JwtInterface => {
    if (AuthUtils.isExpired()) {
      return null;
    }

    return AuthUtils.decode(AuthUtils.getJwt().token);
  };

  public static decode = (token: string): JwtInterface | null => {
    try {
      return jwt_decode<JwtInterface>(token);
    } catch (error) {
      console.error('[AuthUtils] Failed to decode JWT:', error);
      return null;
    }
  };

  public static make = (token: string): AuthJwtObject => {
    const decoded = AuthUtils.decode(token);
    if (!decoded || !decoded.exp) {
      throw new Error('Invalid JWT token: unable to decode or missing expiration');
    }
    return {
      token,
      expires: decoded.exp * 1000,
    };
  };
}

export { AuthUtils };
